import { ChatMessageGroup } from '@provision/chat-utils';
import { useContext, useMemo } from 'react';
import type { ConversationParticipant } from 'src/graphql/entities/conversation/conversation.types';
import { partitionGroupMessagesByTimeWindows } from 'src/pages/Conversations/Chat/partition-group-messages-by-time-window';
import { useTimelineBoundaryTimestamps } from 'src/pages/Conversations/Chat/registry/hooks';
import {
  ChildStatus,
  MessageTypeId,
  MessageTypeRegistration,
  TimelineEntry,
} from 'src/pages/Conversations/Chat/registry/types';
import {
  DEFAULT_TIMEOUT_MS,
  useReadinessWithTimeout,
} from 'src/pages/Conversations/Chat/registry/use-readiness-with-timeout';
import { ChatMessageContext, InitialPhase } from './chat-message-context';
import { ChatMessageProvider } from './chat-message-provider';
import { ChatMessageRow } from './chat-message-row';

function useItems(): TimelineEntry<ChatMessageGroup<ConversationParticipant>>[] {
  const { messagesWithMetadata, initialPhase } = useContext(ChatMessageContext);
  const boundaryTimestamps = useTimelineBoundaryTimestamps();

  return useMemo(() => {
    if (initialPhase !== InitialPhase.Ready) return [];

    return messagesWithMetadata.flatMap((group) =>
      partitionGroupMessagesByTimeWindows<ConversationParticipant>(
        group,
        boundaryTimestamps,
      ).map((partitioned) => ({
        type: MessageTypeId.ChatMessage,
        timestamp: partitioned.createdAt,
        item: partitioned,
      })),
    );
  }, [messagesWithMetadata, initialPhase, boundaryTimestamps]);
}

function useReadiness(): ChildStatus {
  const { isConversationLoading, initialPhase, messagesWithMetadata } =
    useContext(ChatMessageContext);

  // useConversation does not expose an `error` field, so query failures surface
  // as Loading → Timeout after DEFAULT_TIMEOUT_MS rather than entering
  // ChildStatus.Error immediately.
  return useReadinessWithTimeout({
    loading: initialPhase !== InitialPhase.Ready || isConversationLoading,
    hasData: messagesWithMetadata.length > 0,
    timeoutMs: DEFAULT_TIMEOUT_MS,
  });
}

function useLoadMore(): (() => Promise<void>) | undefined {
  const { loadMore } = useContext(ChatMessageContext);
  return loadMore;
}

function usePaginationState(): { hasMore: boolean; isLoadingMore: boolean } {
  const { hasMore, isLoadingMore } = useContext(ChatMessageContext);
  return { hasMore, isLoadingMore };
}

export const chatMessageRegistration: MessageTypeRegistration<
  ChatMessageGroup<ConversationParticipant>
> = {
  id: MessageTypeId.ChatMessage,
  Row: ChatMessageRow,
  Provider: ChatMessageProvider,
  getKey: (item) => item.id,
  useItems,
  useReadiness,
  useLoadMore,
  usePaginationState,
  blockingForInitialRender: true,
  timeoutMs: DEFAULT_TIMEOUT_MS,
};

export default chatMessageRegistration;
